/**
 * Desktop JavaScript
 * Handles desktop icons, taskbar, start menu, and clock
 */

// Prevent redeclaration if script is loaded multiple times
if (typeof DesktopManager === 'undefined') {
    class DesktopManager {
        constructor() {
            this.startMenuOpen = false;
            this.clockInterval = null;
            this.iconClickTimers = new Map(); // Track icon click timers to prevent rapid double-clicks
            this.overflowMenuOpen = false;
            this.overflowTab = null;
            this.overflowMenu = null;
            this.init();
        }

        init() {
            // Load desktop settings
            this.loadDesktopSettings();

            // Check if install wizard has been completed
            const installCompleted = localStorage.getItem('smash_install_completed') === 'true';

            // Desktop UI visibility is now handled by theme.js showDesktopUI() function
            // This manager just sets up functionality, not visibility

            // Setup resize listener for taskbar overflow
            this.setupTaskbarOverflowResize();

            // Move app section icons into desktop-icons container
            this.moveAppIconsToDesktop();

            this.setupDesktopIcons();
            this.setupStartMenu();
            this.setupClock();
            this.setupDesktopLogo();
            this.setupMobileBackground();
            this.setupSystemTray();
            this.setupCartUpdates();

            // Update start menu position on resize (for mobile)
            window.addEventListener('resize', () => {
                this.updateStartMenuPosition();
            });

            // Initial position update
            setTimeout(() => {
                this.updateStartMenuPosition();
            }, 100);
        }

        /**
         * Load desktop settings from JSON script tag
         */
        loadDesktopSettings() {
            const settingsScript = document.getElementById('desktop-settings');
            if (!settingsScript) return;

            try {
                const settings = JSON.parse(settingsScript.textContent);
                const desktop = document.getElementById('desktop');
                if (!desktop) return;

                // Apply background image
                if (settings.desktop_bg_image) {
                    desktop.style.setProperty('background-image', `url('${settings.desktop_bg_image}')`, 'important');
                }

                // Apply theme
                if (settings.theme_mode) {
                    desktop.setAttribute('data-theme', settings.theme_mode);
                }

                // Grid settings are now set directly in layout/theme.liquid via Liquid
                // No need to set them here

                // Apply mobile background
                if (settings.desktop_bg_image_mobile) {
                    desktop.setAttribute('data-mobile-bg', settings.desktop_bg_image_mobile);
                }

                // Show volume icon if enabled
                if (settings.show_volume_icon) {
                    const systemTray = document.getElementById('system-tray');
                    if (systemTray && !systemTray.querySelector('#tray-volume')) {
                        const volumeBtn = document.createElement('button');
                        volumeBtn.className = 'st-taskbar__tray-icon';
                        volumeBtn.id = 'tray-volume';
                        volumeBtn.setAttribute('aria-label', 'Volume control');
                        volumeBtn.title = 'Volume';
                        volumeBtn.innerHTML = '<span class="st-taskbar__tray-icon-label">🔊</span>';
                        systemTray.insertBefore(volumeBtn, systemTray.querySelector('#taskbar-clock'));
                    }
                }
            } catch (e) {
                console.error('DesktopManager: Failed to load desktop settings', e);
            }
        }

        /**
         * Move app section icons into desktop-icons container
         * Retries if icons aren't found yet (sections might not be loaded)
         * Works even when desktop is hidden (display: none doesn't prevent DOM manipulation)
         */
        moveAppIconsToDesktop() {
            if (this._moveIconAttempts === undefined) this._moveIconAttempts = 0;
            const MAX_MOVE_ATTEMPTS = 10;

            const desktopIcons = document.getElementById('desktop-icons');
            if (!desktopIcons) {
                this._moveIconAttempts += 1;
                if (this._moveIconAttempts >= MAX_MOVE_ATTEMPTS) {
                    console.warn('[DesktopManager] desktop-icons container never found; giving up');
                    return;
                }
                setTimeout(() => this.moveAppIconsToDesktop(), 100);
                return;
            }

            const appIcons = document.querySelectorAll('.st-desktop-icon--app');

            if (appIcons.length === 0) {
                this._moveIconAttempts += 1;
                if (this._moveIconAttempts >= MAX_MOVE_ATTEMPTS) {
                    console.warn('[DesktopManager] no app icons found after retries; giving up');
                    return;
                }
                setTimeout(() => this.moveAppIconsToDesktop(), 200);
                return;
            }

            let movedCount = 0;
            appIcons.forEach(icon => {
                if (icon.parentElement !== desktopIcons) {
                    desktopIcons.appendChild(icon);
                    movedCount++;
                }
            });

            if (movedCount > 0 && movedCount < appIcons.length) {
                this._moveIconAttempts += 1;
                if (this._moveIconAttempts >= MAX_MOVE_ATTEMPTS) return;
                setTimeout(() => this.moveAppIconsToDesktop(), 500);
            }
        }

        /**
         * Setup desktop icon click handlers
         */
        setupDesktopIcons() {
            const icons = document.querySelectorAll('.st-desktop-icon');

            icons.forEach(icon => {
                // Single-click handler (works on both desktop and mobile)
                icon.addEventListener('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    // Prevent rapid double-clicks by debouncing
                    const iconId = icon.dataset.sectionId || icon.dataset.actionValue || icon.id || 'icon-' + Math.random();
                    const lastClickTime = this.iconClickTimers.get(iconId) || 0;
                    const now = Date.now();

                    // If clicked within 500ms of last click, ignore
                    if (now - lastClickTime < 500) {
                        console.log('[DesktopManager] Ignoring rapid double-click on icon:', iconId);
                        return;
                    }

                    // Update last click time
                    this.iconClickTimers.set(iconId, now);

                    // Clear the timer after debounce period
                    setTimeout(() => {
                        this.iconClickTimers.delete(iconId);
                    }, 500);

                    this.handleIconClick(icon);
                });

                // Keyboard support
                icon.addEventListener('keydown', (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        this.handleIconClick(icon);
                    }
                });
            });
        }

        /**
         * Handle icon click
         */
        handleIconClick(icon) {
            const actionType = icon.dataset.actionType;
            const sectionId = icon.dataset.sectionId;
            const actionValue = icon.dataset.actionValue;

            // Get actual value based on action type
            let value = actionValue;

            switch (actionType) {
                case 'collection':
                    if (window.smashOS && window.smashOS.windowManager) {
                        // If it's an app section, use section ID, otherwise use collection handle
                        if (sectionId) {
                            window.smashOS.windowManager.openAppWindow('collection-app', sectionId);
                        } else {
                            window.smashOS.windowManager.openCollection(value);
                        }
                    }
                    break;
                case 'product':
                    if (window.smashOS && window.smashOS.windowManager) {
                        // If it's an app section, use section ID, otherwise use product handle
                        if (sectionId) {
                            window.smashOS.windowManager.openAppWindow('product-app', sectionId);
                        } else {
                            window.smashOS.windowManager.openProduct(value);
                        }
                    }
                    break;
                case 'page':
                    if (window.smashOS && window.smashOS.windowManager) {
                        // Special handling for hero banner
                        if (value === 'hero-banner' || value === 'home') {
                            window.smashOS.windowManager.openHeroBanner();
                        } else {
                            window.smashOS.windowManager.openPage(value);
                        }
                    }
                    break;
                case 'hero-banner':
                case 'hero-banner-app':
                    if (window.smashOS && window.smashOS.windowManager && sectionId) {
                        window.smashOS.windowManager.openAppWindow('hero-banner-app', sectionId);
                    } else if (window.smashOS && window.smashOS.windowManager) {
                        // Fallback if no sectionId
                        window.smashOS.windowManager.openHeroBanner();
                    }
                    break;
                case 'about':
                    if (window.smashOS && window.smashOS.windowManager && sectionId) {
                        window.smashOS.windowManager.openAppWindow('about-app', sectionId);
                    }
                    break;
                case 'richtext':
                    if (window.smashOS && window.smashOS.windowManager) {
                        if (sectionId) {
                            // If section ID is provided, open the specific richtext-app section
                            window.smashOS.windowManager.openAppWindow('richtext-app', sectionId);
                        } else if (value) {
                            // If action value is provided (custom ID or section ID), try to find the section
                            let targetSectionId = value;
                            // First try to find by custom ID (data-custom-id)
                            const desktopIconByCustomId = document.querySelector(`[data-action-type="richtext"][data-custom-id="${value}"]`);
                            if (desktopIconByCustomId && desktopIconByCustomId.dataset.sectionId) {
                                targetSectionId = desktopIconByCustomId.dataset.sectionId;
                            } else {
                                // Fallback: try to find by section ID or action value
                                const desktopIcon = document.querySelector(`[data-section-id="${value}"], [data-action-type="richtext"][data-action-value="${value}"]`);
                                if (desktopIcon && desktopIcon.dataset.sectionId) {
                                    targetSectionId = desktopIcon.dataset.sectionId;
                                }
                            }
                            window.smashOS.windowManager.openAppWindow('richtext-app', targetSectionId);
                        }
                    }
                    break;
                case 'media-player':
                    if (window.smashOS && window.smashOS.windowManager && sectionId) {
                        // Open AND start playback, same as the taskbar music button.
                        if (typeof window.smashOS.windowManager.playDopeBeats === 'function') {
                            window.smashOS.windowManager.playDopeBeats();
                        } else {
                            window.smashOS.windowManager.openAppWindow('media-player-app', sectionId);
                        }
                    }
                    break;
                case 'collection-app':
                    if (window.smashOS && window.smashOS.windowManager && sectionId) {
                        window.smashOS.windowManager.openAppWindow('collection-app', sectionId);
                    }
                    break;
                case 'product-app':
                    if (window.smashOS && window.smashOS.windowManager && sectionId) {
                        window.smashOS.windowManager.openAppWindow('product-app', sectionId);
                    }
                    break;
                case 'email':
                    if (window.smashOS && window.smashOS.emailCaptureManager) {
                        window.smashOS.emailCaptureManager.startEmailFlow();
                    }
                    break;
                case 'install':
                    if (window.smashOS && window.smashOS.installManager) {
                        window.smashOS.installManager.showInstallWizard();
                    }
                    break;
                case 'account':
                    window.location.href = '/account';
                    break;
                case 'cart':
                    if (window.smashOS && window.smashOS.windowManager) {
                        window.smashOS.windowManager.openCart();
                    }
                    break;
                case 'url':
                    if (value) {
                        window.open(value, '_blank');
                    }
                    break;
                case 'mailto':
                    if (value) {
                        const mailtoUrl = value.startsWith('mailto:') ? value : `mailto:${value}`;
                        window.location.href = mailtoUrl;
                    }
                    break;
                case 'close-all-windows':
                    if (window.smashOS && window.smashOS.windowManager) {
                        window.smashOS.windowManager.closeAllWindows();
                    }
                    break;
                default:
                    console.warn('Unknown action type:', actionType);
            }
        }

        /**
         * Setup start menu
         */
        setupStartMenu() {
            const startButton = document.getElementById('start-button');
            const startMenu = document.getElementById('start-menu');

            if (!startButton || !startMenu) return;

            // Populate start menu from blocks
            this.populateStartMenu();

            // Toggle start menu
            startButton.addEventListener('click', (e) => {
                e.stopPropagation();
                this.toggleStartMenu();
            });

            // Close menu when clicking outside
            document.addEventListener('click', (e) => {
                if (!startMenu.contains(e.target) && !startButton.contains(e.target)) {
                    this.closeStartMenu();
                }
            });

            // Close menu on ESC key
            document.addEventListener('keydown', (e) => {
                if (e.key === 'Escape' && this.startMenuOpen) {
                    this.closeStartMenu();
                }
            });

            // Setup menu item clicks - use event delegation to handle dynamically added items
            startMenu.addEventListener('click', (e) => {
                const menuItem = e.target.closest('.st-start-menu__item');
                if (!menuItem) return;

                // Get all data attributes like desktop icons
                const actionType = menuItem.dataset.actionType;
                const sectionId = menuItem.dataset.sectionId;
                const actionValue = menuItem.dataset.actionValue;

                // Use the same handler as desktop icons for consistency
                this.handleMenuAction(actionType, actionValue, sectionId);
                this.closeStartMenu();
            });
        }

        /**
         * Populate start menu from section blocks
         */
        populateStartMenu() {
            const startMenu = document.getElementById('start-menu');
            const menuItemsScript = document.getElementById('start-menu-items');

            if (!startMenu || !menuItemsScript) return;

            try {
                const menuItems = JSON.parse(menuItemsScript.textContent);

                // Clear existing items
                startMenu.innerHTML = '';

                // Separate logo and regular items
                const logoItem = menuItems.find(item => item.type === 'logo');
                const regularItems = menuItems.filter(item => item.type !== 'logo');

                // Add logo first if it exists
                if (logoItem && logoItem.logo_image) {
                    const logoDiv = document.createElement('div');
                    logoDiv.className = 'st-start-menu__logo';
                    const logoImg = document.createElement('img');
                    logoImg.src = logoItem.logo_image;
                    logoImg.alt = '';
                    logoImg.className = 'st-start-menu__logo-image';
                    logoDiv.appendChild(logoImg);
                    startMenu.appendChild(logoDiv);
                }

                // Create menu items from blocks
                regularItems.forEach(item => {
                    const menuItem = document.createElement('button');
                    menuItem.className = 'st-start-menu__item';
                    menuItem.setAttribute('role', 'menuitem');
                    menuItem.setAttribute('tabindex', '0');

                    // Set data attributes
                    if (item.action_type) {
                        menuItem.setAttribute('data-action-type', item.action_type);
                    }
                    if (item.action_value) {
                        menuItem.setAttribute('data-action-value', item.action_value);
                    }
                    if (item.section_id) {
                        menuItem.setAttribute('data-section-id', item.section_id);
                    }

                    // Add icon if available
                    if (item.icon) {
                        const icon = document.createElement('img');
                        icon.className = 'st-start-menu__icon';
                        icon.src = item.icon;
                        icon.alt = '';
                        icon.width = 16;
                        icon.height = 16;
                        menuItem.appendChild(icon);
                    }

                    // Add label
                    const label = document.createElement('span');
                    label.className = 'st-start-menu__label';
                    label.textContent = item.label || 'Menu Item';
                    menuItem.appendChild(label);

                    startMenu.appendChild(menuItem);
                });
            } catch (error) {
                console.error('DesktopManager: Error parsing start menu items:', error);
            }
        }

        /**
         * Update start menu position on mobile
         */
        updateStartMenuPosition() {
            const startMenu = document.getElementById('start-menu');
            if (!startMenu) return;

            // Always update position on mobile, even when closed
            if (window.innerWidth <= 767) {
                const taskbar = document.getElementById('taskbar');
                if (taskbar) {
                    // Use multiple methods to get accurate height
                    const taskbarRect = taskbar.getBoundingClientRect();
                    const taskbarHeight = taskbarRect.height || taskbar.offsetHeight || 48;

                    // Ensure fixed positioning on mobile
                    startMenu.style.setProperty('position', 'fixed', 'important');
                    startMenu.style.setProperty('left', '0', 'important');
                    startMenu.style.setProperty('right', 'auto', 'important');

                    // Position above taskbar
                    startMenu.style.setProperty('bottom', `${taskbarHeight}px`, 'important');
                    startMenu.style.setProperty('max-height', `calc(100vh - ${taskbarHeight}px - 20px)`, 'important');
                }
            } else {
                // Reset on desktop - use absolute positioning
                startMenu.style.setProperty('position', 'absolute', 'important');
                startMenu.style.removeProperty('bottom');
                startMenu.style.removeProperty('max-height');
                startMenu.style.removeProperty('left');
                startMenu.style.removeProperty('right');
            }
        }

        /**
         * Toggle start menu
         */
        toggleStartMenu() {
            const startMenu = document.getElementById('start-menu');
            const startButton = document.getElementById('start-button');

            if (!startMenu || !startButton) return;

            this.startMenuOpen = !this.startMenuOpen;

            // Update position before showing (especially important on mobile)
            this.updateStartMenuPosition();

            // Small delay to ensure taskbar is fully rendered
            if (window.innerWidth <= 767 && this.startMenuOpen) {
                setTimeout(() => {
                    this.updateStartMenuPosition();
                }, 10);
            }

            startMenu.setAttribute('aria-hidden', !this.startMenuOpen);
            startButton.setAttribute('aria-expanded', this.startMenuOpen);
        }

        /**
         * Close start menu
         */
        closeStartMenu() {
            const startMenu = document.getElementById('start-menu');
            const startButton = document.getElementById('start-button');

            if (!startMenu || !startButton) return;

            this.startMenuOpen = false;
            startMenu.setAttribute('aria-hidden', 'true');
            startButton.setAttribute('aria-expanded', 'false');
        }

        /**
         * Handle menu action
         */
        handleMenuAction(actionType, actionValue, sectionId) {
            // Use the same logic as handleIconClick for consistency
            // Get actual value based on action type
            let value = actionValue;

            switch (actionType) {
                case 'collection':
                    if (window.smashOS && window.smashOS.windowManager) {
                        // If it's an app section, use section ID, otherwise use collection handle
                        if (sectionId) {
                            window.smashOS.windowManager.openAppWindow('collection-app', sectionId);
                        } else {
                            window.smashOS.windowManager.openCollection(value);
                        }
                    }
                    break;
                case 'product':
                    if (window.smashOS && window.smashOS.windowManager) {
                        // If it's an app section, use section ID, otherwise use product handle
                        if (sectionId) {
                            window.smashOS.windowManager.openAppWindow('product-app', sectionId);
                        } else {
                            window.smashOS.windowManager.openProduct(value);
                        }
                    }
                    break;
                case 'page':
                    if (window.smashOS && window.smashOS.windowManager) {
                        // Special handling for hero banner
                        if (value === 'hero-banner' || value === 'home') {
                            window.smashOS.windowManager.openHeroBanner();
                        } else {
                            window.smashOS.windowManager.openPage(value);
                        }
                    }
                    break;
                case 'hero-banner':
                case 'hero-banner-app':
                    if (window.smashOS && window.smashOS.windowManager && sectionId) {
                        window.smashOS.windowManager.openAppWindow('hero-banner-app', sectionId);
                    } else if (window.smashOS && window.smashOS.windowManager) {
                        // Fallback if no sectionId
                        window.smashOS.windowManager.openHeroBanner();
                    }
                    break;
                case 'about':
                    if (window.smashOS && window.smashOS.windowManager && sectionId) {
                        window.smashOS.windowManager.openAppWindow('about-app', sectionId);
                    }
                    break;
                case 'richtext':
                    if (window.smashOS && window.smashOS.windowManager) {
                        if (sectionId) {
                            // If section ID is provided, open the specific richtext-app section
                            window.smashOS.windowManager.openAppWindow('richtext-app', sectionId);
                        } else if (value) {
                            // If action value is provided (custom ID or section ID), try to find the section
                            let targetSectionId = value;
                            // First try to find by custom ID (data-custom-id)
                            const desktopIconByCustomId = document.querySelector(`[data-action-type="richtext"][data-custom-id="${value}"]`);
                            if (desktopIconByCustomId && desktopIconByCustomId.dataset.sectionId) {
                                targetSectionId = desktopIconByCustomId.dataset.sectionId;
                            } else {
                                // Fallback: try to find by section ID or action value
                                const desktopIcon = document.querySelector(`[data-section-id="${value}"], [data-action-type="richtext"][data-action-value="${value}"]`);
                                if (desktopIcon && desktopIcon.dataset.sectionId) {
                                    targetSectionId = desktopIcon.dataset.sectionId;
                                }
                            }
                            window.smashOS.windowManager.openAppWindow('richtext-app', targetSectionId);
                        }
                    }
                    break;
                case 'media-player':
                    if (window.smashOS && window.smashOS.windowManager && sectionId) {
                        // Open AND start playback, same as the taskbar music button.
                        if (typeof window.smashOS.windowManager.playDopeBeats === 'function') {
                            window.smashOS.windowManager.playDopeBeats();
                        } else {
                            window.smashOS.windowManager.openAppWindow('media-player-app', sectionId);
                        }
                    }
                    break;
                case 'collection-app':
                    if (window.smashOS && window.smashOS.windowManager && sectionId) {
                        window.smashOS.windowManager.openAppWindow('collection-app', sectionId);
                    }
                    break;
                case 'product-app':
                    if (window.smashOS && window.smashOS.windowManager && sectionId) {
                        window.smashOS.windowManager.openAppWindow('product-app', sectionId);
                    }
                    break;
                case 'email':
                    if (window.smashOS && window.smashOS.emailCaptureManager) {
                        window.smashOS.emailCaptureManager.startEmailFlow();
                    }
                    break;
                case 'install':
                    if (window.smashOS && window.smashOS.installManager) {
                        window.smashOS.installManager.showInstallWizard();
                    }
                    break;
                case 'account':
                    window.location.href = '/account';
                    break;
                case 'cart':
                    if (window.smashOS && window.smashOS.windowManager) {
                        window.smashOS.windowManager.openCart();
                    }
                    break;
                case 'url':
                    if (value) {
                        window.open(value, '_blank');
                    }
                    break;
                case 'mailto':
                    if (value) {
                        const mailtoUrl = value.startsWith('mailto:') ? value : `mailto:${value}`;
                        window.location.href = mailtoUrl;
                    }
                    break;
                case 'close-all-windows':
                    if (window.smashOS && window.smashOS.windowManager) {
                        window.smashOS.windowManager.closeAllWindows();
                    }
                    break;
                default:
                    console.warn('Unknown action type:', actionType);
            }
        }

        /**
         * Setup clock
         */
        setupClock() {
            const clockElement = document.getElementById('clock-time');
            if (!clockElement) return;

            const updateClock = () => {
                const now = new Date();
                const hours = String(now.getHours()).padStart(2, '0');
                const minutes = String(now.getMinutes()).padStart(2, '0');
                clockElement.textContent = `${hours}:${minutes}`;
            };

            // Update immediately
            updateClock();

            // Update every minute
            this.clockInterval = setInterval(updateClock, 60000);
        }

        /**
         * Setup desktop logo click handler to minimize all windows
         */
        setupDesktopLogo() {
            const desktopLogo = document.getElementById('desktop-logo');
            if (!desktopLogo) return;

            // Make logo clickable
            desktopLogo.style.cursor = 'pointer';
            desktopLogo.setAttribute('role', 'button');
            desktopLogo.setAttribute('tabindex', '0');
            desktopLogo.setAttribute('aria-label', 'Minimize all windows');

            // Add click handler
            desktopLogo.addEventListener('click', () => {
                if (window.smashOS && window.smashOS.windowManager) {
                    window.smashOS.windowManager.minimizeAllWindows();
                }
            });

            // Add keyboard support
            desktopLogo.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    if (window.smashOS && window.smashOS.windowManager) {
                        window.smashOS.windowManager.minimizeAllWindows();
                    }
                }
            });
        }

        /**
         * Setup mobile background image
         */
        setupMobileBackground() {
            const desktop = document.getElementById('desktop');
            if (!desktop) return;

            // Check if mobile background is set via CSS variable
            // This would be set from Liquid template based on section settings
            const mobileBg = desktop.dataset.mobileBg;
            if (mobileBg && window.innerWidth <= 767) {
                desktop.style.backgroundImage = `url('${mobileBg}')`;
            }
        }

        /**
         * Add window to taskbar
         */
        addWindowToTaskbar(windowId, title, icon) {
            const taskbarWindows = document.getElementById('taskbar-windows');
            if (!taskbarWindows) return;

            // Check if window already exists in taskbar
            const existingItem = document.getElementById(`taskbar-window-${windowId}`);
            if (existingItem) {
                // Update existing item
                this.updateWindowTaskbarState(windowId, true);
                return existingItem;
            }

            const windowItem = document.createElement('button');
            windowItem.className = 'st-taskbar__window-item';
            windowItem.id = `taskbar-window-${windowId}`;
            windowItem.setAttribute('aria-label', `Switch to ${title}`);
            windowItem.dataset.windowId = windowId;

            if (icon) {
                const iconImg = document.createElement('img');
                iconImg.src = icon;
                iconImg.alt = '';
                iconImg.className = 'st-taskbar__window-icon';
                iconImg.width = 24;
                iconImg.height = 24;
                windowItem.appendChild(iconImg);
            }

            const titleSpan = document.createElement('span');
            titleSpan.textContent = title;
            titleSpan.style.overflow = 'hidden';
            titleSpan.style.textOverflow = 'ellipsis';
            titleSpan.style.maxWidth = '200px';
            windowItem.appendChild(titleSpan);

            windowItem.addEventListener('click', (e) => {
                e.stopPropagation();
                if (window.smashOS && window.smashOS.windowManager) {
                    window.smashOS.windowManager.bringToFront(windowId);
                }
            });

            // Right-click context menu (optional, for future enhancement)
            windowItem.addEventListener('contextmenu', (e) => {
                e.preventDefault();
                // Could show context menu with close/minimize options
            });

            taskbarWindows.appendChild(windowItem);

            // Update overflow after adding window
            this.updateTaskbarOverflow();

            return windowItem;
        }

        /**
         * Remove window from taskbar
         */
        removeWindowFromTaskbar(windowId) {
            const windowItem = document.getElementById(`taskbar-window-${windowId}`);
            if (windowItem) {
                windowItem.remove();
            }
            // Update overflow after removing window
            this.updateTaskbarOverflow();
        }

        /**
         * Update window taskbar item state
         * When setting a window as active, removes active state from all other windows
         */
        updateWindowTaskbarState(windowId, isActive) {
            // If setting active, first remove active state from all taskbar items
            if (isActive) {
                document.querySelectorAll('.st-taskbar__window-item').forEach(item => {
                    item.classList.remove('st-taskbar__window-item--active');
                });
            }

            // Then update the specific window item
            const windowItem = document.getElementById(`taskbar-window-${windowId}`);
            if (windowItem) {
                if (isActive) {
                    windowItem.classList.add('st-taskbar__window-item--active');
                } else {
                    windowItem.classList.remove('st-taskbar__window-item--active');
                }
            }
        }

        /**
         * Setup system tray
         */
        setupSystemTray() {
            // System tray icons are already in the HTML
            // This method can be extended for additional tray functionality
        }

        /**
         * Setup cart updates
         */
        setupCartUpdates() {
            // Update cart count from Shopify cart
            this.updateCartCount();

            // Listen for cart updates (if using Shopify Ajax API)
            document.addEventListener('cart:updated', () => {
                this.updateCartCount();
            });
        }

        /**
         * Update taskbar overflow - hide windows that don't fit and show overflow tab
         */
        updateTaskbarOverflow() {
            const taskbarWindows = document.getElementById('taskbar-windows');
            if (!taskbarWindows) return;

            // Get all window items (excluding overflow tab)
            const windowItems = Array.from(taskbarWindows.querySelectorAll('.st-taskbar__window-item')).filter(
                item => !item.classList.contains('st-taskbar__overflow-tab')
            );

            if (windowItems.length === 0) {
                // Remove overflow tab if no windows
                this.removeOverflowTab();
                return;
            }

            // Get container dimensions
            // The taskbar__windows container is a flex item, so its width is already the available space
            const containerRect = taskbarWindows.getBoundingClientRect();
            const containerWidth = containerRect.width;

            // Account for padding and gaps within the container
            const containerPadding = parseFloat(getComputedStyle(taskbarWindows).paddingLeft) +
                parseFloat(getComputedStyle(taskbarWindows).paddingRight) || 0;
            const gap = parseFloat(getComputedStyle(taskbarWindows).gap) || 8; // Default gap
            const availableWidth = containerWidth - containerPadding;

            // Remove overflow tab temporarily to measure accurately
            const existingOverflowTab = this.overflowTab;
            if (existingOverflowTab) {
                existingOverflowTab.remove();
                this.overflowTab = null;
            }

            // Show all windows first to measure them
            windowItems.forEach(item => {
                item.classList.remove('st-taskbar__window-item--hidden');
            });

            // First, try to fit all items without overflow tab
            let totalWidth = 0;

            // Calculate total width of all items (including gaps)
            windowItems.forEach((item, index) => {
                const itemWidth = item.offsetWidth;
                const gapSpace = index > 0 ? gap : 0;
                totalWidth += (gapSpace + itemWidth);
            });

            // If all items fit, no overflow needed
            if (totalWidth <= availableWidth) {
                // All items fit - ensure none are hidden
                windowItems.forEach(item => {
                    item.classList.remove('st-taskbar__window-item--hidden');
                });
                this.removeOverflowTab();
                return;
            }

            // Items don't all fit - need overflow tab
            // Measure overflow tab width first
            const tempOverflowTab = document.createElement('button');
            tempOverflowTab.className = 'st-taskbar__window-item st-taskbar__overflow-tab';
            tempOverflowTab.textContent = '99 windows'; // Use max expected text for measurement
            tempOverflowTab.style.visibility = 'hidden';
            tempOverflowTab.style.position = 'absolute';
            taskbarWindows.appendChild(tempOverflowTab);
            const overflowTabWidth = tempOverflowTab.offsetWidth;
            tempOverflowTab.remove();

            // Now determine which items fit with overflow tab space reserved
            totalWidth = 0;
            const visibleItems = [];
            const hiddenItems = [];

            for (let i = 0; i < windowItems.length; i++) {
                const item = windowItems[i];
                const itemWidth = item.offsetWidth;
                const gapSpace = visibleItems.length > 0 ? gap : 0;
                // Reserve space for overflow tab (width + gap)
                const spaceNeeded = totalWidth + gapSpace + itemWidth + overflowTabWidth + gap;

                if (spaceNeeded <= availableWidth) {
                    totalWidth += (gapSpace + itemWidth);
                    visibleItems.push(item);
                } else {
                    hiddenItems.push(item);
                    item.classList.add('st-taskbar__window-item--hidden');
                }
            }

            // Show or hide overflow tab (will be appended after visible items)
            if (hiddenItems.length > 0) {
                this.showOverflowTab(hiddenItems.length);
            } else {
                this.removeOverflowTab();
            }

            // Update overflow menu with hidden windows
            this.updateOverflowMenu(hiddenItems);
        }

        /**
         * Show overflow tab with count
         */
        showOverflowTab(count) {
            const taskbarWindows = document.getElementById('taskbar-windows');
            if (!taskbarWindows) return;

            // Remove existing overflow tab if any
            this.removeOverflowTab();

            // Create overflow tab
            this.overflowTab = document.createElement('button');
            this.overflowTab.className = 'st-taskbar__window-item st-taskbar__overflow-tab';
            this.overflowTab.id = 'taskbar-overflow-tab';
            this.overflowTab.setAttribute('aria-label', `${count} more windows`);
            this.overflowTab.textContent = `${count} windows`;
            this.overflowTab.addEventListener('click', (e) => {
                e.stopPropagation();
                this.toggleOverflowMenu();
            });

            // Find the last visible window item and insert overflow tab after it
            const visibleItems = Array.from(taskbarWindows.querySelectorAll('.st-taskbar__window-item:not(.st-taskbar__window-item--hidden):not(.st-taskbar__overflow-tab)'));
            if (visibleItems.length > 0) {
                const lastVisibleItem = visibleItems[visibleItems.length - 1];
                lastVisibleItem.insertAdjacentElement('afterend', this.overflowTab);
            } else {
                // If no visible items, just append
                taskbarWindows.appendChild(this.overflowTab);
            }
        }

        /**
         * Remove overflow tab
         */
        removeOverflowTab() {
            if (this.overflowTab) {
                this.overflowTab.remove();
                this.overflowTab = null;
            }
            // Close overflow menu if open
            if (this.overflowMenuOpen) {
                this.closeOverflowMenu();
            }
        }

        /**
         * Update overflow menu with hidden windows
         */
        updateOverflowMenu(hiddenItems) {
            if (!this.overflowMenu) {
                this.createOverflowMenu();
            }

            // Clear existing items
            this.overflowMenu.innerHTML = '';

            // Add items for hidden windows
            hiddenItems.forEach(item => {
                const windowId = item.dataset.windowId;
                if (!windowId) return;

                // Get window title from the item's text content (span element)
                const titleSpan = item.querySelector('span');
                const windowTitle = titleSpan ? titleSpan.textContent.trim() : item.textContent.trim();

                const menuItem = document.createElement('button');
                menuItem.className = 'st-taskbar__overflow-item';
                menuItem.setAttribute('role', 'menuitem');
                menuItem.setAttribute('aria-label', `Switch to ${windowTitle}`);

                // Get icon from the hidden item
                const iconImg = item.querySelector('.st-taskbar__window-icon');
                if (iconImg) {
                    const menuIcon = iconImg.cloneNode(true);
                    menuIcon.className = 'st-taskbar__overflow-icon';
                    menuItem.appendChild(menuIcon);
                }

                const label = document.createElement('span');
                label.className = 'st-taskbar__overflow-label';
                label.textContent = windowTitle;
                menuItem.appendChild(label);

                menuItem.addEventListener('click', (e) => {
                    e.stopPropagation();
                    if (window.smashOS && window.smashOS.windowManager) {
                        window.smashOS.windowManager.bringToFront(windowId);
                        this.closeOverflowMenu();
                    }
                });

                this.overflowMenu.appendChild(menuItem);
            });
        }

        /**
         * Create overflow menu element
         */
        createOverflowMenu() {
            const taskbar = document.getElementById('taskbar');
            if (!taskbar) return;

            // Remove existing menu if any
            if (this.overflowMenu) {
                this.overflowMenu.remove();
            }
            if (this._overflowClickListener) {
                document.removeEventListener('click', this._overflowClickListener);
                this._overflowClickListener = null;
            }

            this.overflowMenu = document.createElement('nav');
            this.overflowMenu.className = 'st-taskbar__overflow-menu';
            this.overflowMenu.id = 'taskbar-overflow-menu';
            this.overflowMenu.setAttribute('role', 'menu');
            this.overflowMenu.setAttribute('aria-label', 'Overflow windows');
            this.overflowMenu.setAttribute('aria-hidden', 'true');

            taskbar.appendChild(this.overflowMenu);

            // Close menu when clicking outside
            this._overflowClickListener = (e) => {
                if (this.overflowMenuOpen &&
                    !this.overflowMenu.contains(e.target) &&
                    !this.overflowTab?.contains(e.target)) {
                    this.closeOverflowMenu();
                }
            };
            document.addEventListener('click', this._overflowClickListener);
        }

        /**
         * Toggle overflow menu
         */
        toggleOverflowMenu() {
            if (this.overflowMenuOpen) {
                this.closeOverflowMenu();
            } else {
                this.openOverflowMenu();
            }
        }

        /**
         * Open overflow menu
         */
        openOverflowMenu() {
            if (!this.overflowMenu || !this.overflowTab) return;

            const taskbar = document.getElementById('taskbar');
            if (!taskbar) return;

            // Position menu to align with left edge of overflow tab
            const overflowTabRect = this.overflowTab.getBoundingClientRect();
            const taskbarRect = taskbar.getBoundingClientRect();

            // Calculate left position relative to taskbar
            const leftPosition = overflowTabRect.left - taskbarRect.left;
            this.overflowMenu.style.left = `${leftPosition}px`;
            this.overflowMenu.style.right = 'auto';

            // Calculate bottom position based on actual taskbar height
            const taskbarHeight = taskbar.offsetHeight || taskbarRect.height;
            this.overflowMenu.style.bottom = `${taskbarHeight}px`;

            // Update max-height to account for actual taskbar height
            this.overflowMenu.style.maxHeight = `calc(100vh - ${taskbarHeight}px - 20px)`;

            this.overflowMenuOpen = true;
            this.overflowMenu.setAttribute('aria-hidden', 'false');
            this.overflowTab.classList.add('st-taskbar__window-item--active');
        }

        /**
         * Close overflow menu
         */
        closeOverflowMenu() {
            if (!this.overflowMenu) return;
            this.overflowMenuOpen = false;
            this.overflowMenu.setAttribute('aria-hidden', 'true');
            if (this.overflowTab) {
                this.overflowTab.classList.remove('st-taskbar__window-item--active');
            }
        }

        /**
         * Setup resize listener for taskbar overflow updates
         */
        setupTaskbarOverflowResize() {
            let resizeTimeout;
            window.addEventListener('resize', () => {
                clearTimeout(resizeTimeout);
                resizeTimeout = setTimeout(() => {
                    this.updateTaskbarOverflow();
                }, 150);
            });

            // Also update when taskbar becomes visible
            const taskbar = document.getElementById('taskbar');
            if (taskbar) {
                const observer = new MutationObserver(() => {
                    if (taskbar.classList.contains('visible')) {
                        // Small delay to ensure layout is complete
                        setTimeout(() => {
                            this.updateTaskbarOverflow();
                        }, 100);
                    }
                });
                observer.observe(taskbar, {
                    attributes: true,
                    attributeFilter: ['class']
                });
            }
        }

        /**
         * Update cart count badge
         */
        async updateCartCount() {
            const cartBadge = document.getElementById('cart-count-badge');
            const cartIcon = document.getElementById('tray-cart');
            const desktopCartIcon = document.getElementById('desktop-cart-icon');
            const desktopCartIconImage = document.getElementById('desktop-cart-icon-image');

            if (!cartBadge || !cartIcon) return;

            try {
                // Fetch cart from Shopify
                const response = await fetch('/cart.js');
                if (response.ok) {
                    const cart = await response.json();
                    const itemCount = cart.item_count || 0;

                    if (itemCount > 0) {
                        cartBadge.textContent = itemCount > 99 ? '99+' : itemCount;
                        cartBadge.setAttribute('aria-hidden', 'false');
                        cartIcon.setAttribute('data-cart-count', itemCount);
                        // Update desktop cart icon to show cart.png when items exist
                        if (desktopCartIconImage && window.smashOSAssets) {
                            desktopCartIconImage.src = window.smashOSAssets.cartIcon;
                        }
                        // Update desktop cart count
                        const desktopCartCount = document.getElementById('desktop-cart-count');
                        if (desktopCartCount) {
                            desktopCartCount.textContent = itemCount > 99 ? '99+' : itemCount;
                        }
                    } else {
                        cartBadge.setAttribute('aria-hidden', 'true');
                        cartIcon.setAttribute('data-cart-count', '0');
                        // Update desktop cart icon to show empty_cart.png when cart is empty
                        if (desktopCartIconImage && window.smashOSAssets) {
                            desktopCartIconImage.src = window.smashOSAssets.emptyCartIcon;
                        }
                        // Update desktop cart count
                        const desktopCartCount = document.getElementById('desktop-cart-count');
                        if (desktopCartCount) {
                            desktopCartCount.textContent = '0';
                        }
                    }
                }
            } catch (error) {
                console.warn('Could not fetch cart count:', error);
            }
        }

        /**
         * Cleanup
         */
        destroy() {
            if (this.clockInterval) {
                clearInterval(this.clockInterval);
            }
        }
    }

    // Export to global scope
    window.DesktopManager = DesktopManager;
}

// Initialize when DOM is ready (only if not already initialized)
if (!window.smashOS?.desktopManager) {
    const initDesktop = () => {
        window.smashOS = window.smashOS || {};
        if (!window.smashOS.desktopManager) {
            window.smashOS.desktopManager = new DesktopManager();
        }
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initDesktop);
    } else {
        initDesktop();
    }
}

